import { ArrowTooltip } from '@/components/arrow-tooltip.js';
import { Info } from 'lucide-react';
import { useState } from 'react';

import type { CoachPoolWithMatchCount } from '@airreading/common/data';

import { CoachPoolDrawer } from './coach-pool-drawer.js';
import { teacherPoolSummary } from './teacher-pool-summary.js';
import { ViewedTeacherPool } from './viewed-teacher-pool.js';

interface TeacherPoolsSectionProps {
  pools: CoachPoolWithMatchCount[];
  poolsLoading: boolean;
  poolsError: boolean;
  activePoolCount: number;
  distinctReservedTeacherCount: number;
  onViewedPoolChange: (pool: CoachPoolWithMatchCount | null) => void;
  isEditingPool?: boolean;
  hasUnsavedChanges?: () => boolean;
  onEditPool: () => void;
  onCancelEdit: () => void;
  onCreatePool: () => void;
  onSavePool?: () => void;
  onCancelCreate?: () => void;
  saveDisabled?: boolean;
  isPoolFormOpen?: boolean;
}

// Section of the page that displays the teacher pools when no pool is selected
export const TeacherPoolsSection = ({
  pools,
  poolsLoading,
  poolsError,
  activePoolCount,
  distinctReservedTeacherCount,
  onViewedPoolChange,
  isEditingPool = false,
  hasUnsavedChanges,
  onEditPool,
  onCancelEdit,
  onCreatePool,
  onSavePool,
  onCancelCreate,
  saveDisabled = false,
  isPoolFormOpen = false,
}: TeacherPoolsSectionProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewedPoolId, setViewedPoolId] = useState<string | null>(null);
  const viewedPool = pools.find((pool) => pool.id === viewedPoolId) ?? null;
  const headingId = viewedPool ? `viewed-teacher-pool-${viewedPool.id}-heading` : 'teacher-availabilities-pool-heading';

  return (
    <section
      aria-labelledby={headingId}
      className="mx-2 mb-2 flex shrink-0 items-center justify-between gap-4 rounded-md border bg-white p-3"
    >
      {viewedPool ? (
        <ViewedTeacherPool
          pool={viewedPool}
          isEditing={isEditingPool}
          hasUnsavedChanges={hasUnsavedChanges}
          onEdit={onEditPool}
          onCancelEdit={onCancelEdit}
          onBack={() => {
            setViewedPoolId(null);
            onViewedPoolChange(null);
          }}
        />
      ) : (
        <div>
          <div className="flex items-center gap-1 pb-1">
            <h2 id="teacher-availabilities-pool-heading" className="text-xl font-semibold">
              Reserved teacher pools
            </h2>
            <ArrowTooltip
              asChild
              trigger={
                <button type="button" aria-label="About reserved teacher pools" className="cursor-pointer">
                  <Info className="h-4 w-4" aria-hidden />
                </button>
              }
            >
              <p className="max-w-[300px] text-left">
                Eligible reserved teachers are used only when no eligible non-reserved teacher can be assigned.
              </p>
            </ArrowTooltip>
          </div>
          <p className="text-sm text-muted-foreground" role={poolsLoading ? 'status' : undefined}>
            {poolsLoading
              ? 'Loading teacher pools'
              : poolsError
                ? 'Teacher pools could not be loaded'
                : teacherPoolSummary(activePoolCount, distinctReservedTeacherCount)}
          </p>
        </div>
      )}
      <CoachPoolDrawer
        pools={pools}
        poolsLoading={poolsLoading}
        poolsError={poolsError}
        drawerOpen={drawerOpen}
        onDrawerOpenChange={setDrawerOpen}
        showTriggers={!viewedPool}
        isPoolFormOpen={isPoolFormOpen}
        onCreatePool={onCreatePool}
        onSavePool={onSavePool}
        onCancelCreate={onCancelCreate}
        saveDisabled={saveDisabled}
        onViewPool={(pool) => {
          setViewedPoolId(pool.id);
          setDrawerOpen(false);
          onViewedPoolChange(pool);
        }}
      />
    </section>
  );
};
